import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface LocationState {
  achievements: string[];
  totalPoints: number;
}

const WaiverCompletePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showFireworks, setShowFireworks] = useState(true);
  
  const state = location.state as LocationState;
  const { achievements = [], totalPoints = 0 } = state || {};

  useEffect(() => {
    // Hide fireworks after 5 seconds
    const timer = setTimeout(() => setShowFireworks(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const achievementEmojis: { [key: string]: string } = {
    'First Steps': '🚀',
    'Halfway Hero': '⭐',
    'VR Pioneer': '🏆'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 relative overflow-hidden">
      {/* Fireworks Animation */}
      {showFireworks && (
        <div className="fixed inset-0 z-10 pointer-events-none">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className={`absolute text-4xl animate-bounce`}
              style={{
                left: `${Math.random() * 90}%`,
                top: `${Math.random() * 80}%`,
                animationDelay: `${i * 0.2}s`,
                animationDuration: `${2 + Math.random() * 2}s`
              }}
            >
              {['🎆', '✨', '🎉', '🎊', '⭐'][Math.floor(Math.random() * 5)]}
            </div>
          ))}
        </div>
      )}

      <div className="container mx-auto px-4 py-12 relative z-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Success Header */}
          <div className="mb-12">
            <div className="text-8xl mb-6 animate-bounce">🎉</div>
            <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-green-400 via-blue-500 to-purple-600 bg-clip-text text-transparent">
              VR Passport Complete!
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              Congratulations! You've successfully completed your VR safety waiver and earned{' '}
              <span className="text-yellow-400 font-bold">{totalPoints} points</span>!
            </p>
          </div>

          {/* Achievement Display */}
          {achievements.length > 0 && (
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-8 border border-white/20">
              <h2 className="text-3xl font-bold text-white mb-6">🏆 Achievements Unlocked</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {achievements.map((achievement, index) => (
                  <div
                    key={achievement}
                    className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-xl p-6 border border-yellow-400/30 transform hover:scale-105 transition-all duration-300"
                    style={{ animationDelay: `${index * 0.5}s` }}
                  >
                    <div className="text-4xl mb-3">
                      {achievementEmojis[achievement] || '🏅'}
                    </div>
                    <h3 className="text-lg font-bold text-yellow-400 mb-2">
                      {achievement}
                    </h3>
                    <p className="text-gray-300 text-sm">
                      {achievement === 'First Steps' && 'Started your VR journey!'}
                      {achievement === 'Halfway Hero' && 'Made great progress!'}
                      {achievement === 'VR Pioneer' && 'Completed everything perfectly!'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-8 border border-white/20">
            <h2 className="text-3xl font-bold text-white mb-6">🎮 What's Next?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="text-left">
                <div className="text-2xl mb-3">📧</div>
                <h3 className="text-lg font-bold text-purple-400 mb-2">Check Your Email</h3>
                <p className="text-gray-300">
                  We've sent you a confirmation email with your QR code and booking instructions.
                </p>
              </div>
              <div className="text-left">
                <div className="text-2xl mb-3">📱</div>
                <h3 className="text-lg font-bold text-blue-400 mb-2">Save Your QR Code</h3>
                <p className="text-gray-300">
                  Present your QR code when you arrive to skip the check-in process!
                </p>
              </div>
              <div className="text-left">
                <div className="text-2xl mb-3">🎯</div>
                <h3 className="text-lg font-bold text-green-400 mb-2">Book Your Session</h3>
                <p className="text-gray-300">
                  Contact us or visit in person to schedule your VR adventure.
                </p>
              </div>
              <div className="text-left">
                <div className="text-2xl mb-3">🎁</div>
                <h3 className="text-lg font-bold text-pink-400 mb-2">Special Offers</h3>
                <p className="text-gray-300">
                  Watch for exclusive discounts and new experience announcements!
                </p>
              </div>
            </div>
          </div>

          {/* Special Rewards */}
          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl p-8 mb-8 border border-purple-400/30">
            <h2 className="text-3xl font-bold text-white mb-4">🎊 Welcome Bonus!</h2>
            <p className="text-xl text-gray-300 mb-4">
              As a thank you for completing your waiver, you've unlocked:
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              <div className="bg-black/30 rounded-full px-6 py-2 text-yellow-400 font-semibold">
                10% Off First Visit
              </div>
              <div className="bg-black/30 rounded-full px-6 py-2 text-green-400 font-semibold">
                Priority Booking
              </div>
              <div className="bg-black/30 rounded-full px-6 py-2 text-blue-400 font-semibold">
                Free STEM Kit
              </div>
            </div>
            <p className="text-sm text-gray-400">
              * Offer valid for 30 days from waiver completion
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              🏠 Return Home
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="bg-gradient-to-r from-blue-600 to-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-teal-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              📞 Book Your Visit
            </button>
            <button
              onClick={() => window.open('https://calendar.google.com', '_blank')}
              className="bg-white/20 backdrop-blur-lg text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/30 transition-all duration-300 border border-white/30"
            >
              📅 Schedule Now
            </button>
          </div>

          {/* Social Sharing */}
          <div className="mt-12 text-center">
            <p className="text-gray-400 mb-4">Share your VR Pioneer achievement!</p>
            <div className="flex justify-center space-x-4">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
                📘 Facebook
              </button>
              <button className="bg-blue-400 hover:bg-blue-500 text-white px-6 py-2 rounded-lg transition-colors">
                🐦 Twitter
              </button>
              <button className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-2 rounded-lg transition-colors">
                📷 Instagram
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaiverCompletePage;